import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Season detail — running Ryder-Cup-style standings for one team season.
 *
 * Top:    big team-A vs team-B points score
 * Middle: per-player breakdown (W/L/H)
 * Bottom: chronological list of matches played
 */
export default function TeamSeasonDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/team-seasons/${id}`);
        if (!cancelled) setSeason(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load season.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this season? Standings will be lost.")) return;
    try {
      await api.delete(`/team-seasons/${id}`);
      nav("/app/seasons");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't delete season.");
    }
  };

  if (loading) return <div className="p-8 text-center italic text-[var(--text-secondary)]">Loading…</div>;
  if (error) return <div className="p-8 text-center text-[var(--accent-terracotta)]" data-testid="season-detail-error">{error}</div>;
  if (!season) return null;

  const s = season.standings || {};
  const aWinning = s.team_a_points > s.team_b_points;
  const bWinning = s.team_b_points > s.team_a_points;

  return (
    <div className="pb-24 max-w-3xl mx-auto" data-testid="season-detail-page">
      <div className="px-5 pt-6">
        <Link to="/app/seasons" className="text-xs font-bold text-[var(--text-secondary)] hover:underline">← Seasons</Link>
        <h1 className="font-heading font-black text-3xl tracking-tighter-2 text-[var(--primary)] mt-1" data-testid="season-detail-name">
          {season.name}
        </h1>
        <p className="text-xs text-[var(--text-secondary)] mt-1 mb-5 font-mono-num">
          {season.start_date} → {season.end_date} · {s.matches_played || 0} match{s.matches_played === 1 ? "" : "es"} played
        </p>

        {/* Share / invite bar. WhatsApp-first because that's how most
            fourball groups already coordinate — one tap, message ready
            to send, join link baked in. Works on desktop too via the
            web WhatsApp fallback. */}
        <div className="mb-5" data-testid="season-invite-bar">
          <button
            type="button"
            onClick={() => {
              const url = `${window.location.origin}/app/seasons/${id}`;
              const text = `Come and battle in the "${season.name}" season on Fairway ⛳ — running Ryder-Cup-style standings, ${season.team_a_name} vs ${season.team_b_name}. Follow the scores live: ${url}`;
              const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
              window.open(waUrl, "_blank", "noopener");
            }}
            data-testid="season-invite-whatsapp"
            className="w-full rounded-2xl py-2.5 px-4 font-heading font-black text-sm text-white active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
            style={{ background: "#25D366" }}
          >
            <span>Invite mates on WhatsApp</span>
            <span className="text-lg">↗</span>
          </button>
          <button
            type="button"
            onClick={async () => {
              const url = `${window.location.origin}/app/seasons/${id}`;
              try {
                await navigator.clipboard.writeText(url);
                setError("");
                // Piggyback on the error slot for a quick success toast.
                setError("✓ Link copied to clipboard");
                setTimeout(() => setError(""), 2200);
              } catch {
                setError("Couldn't copy — long-press the URL bar instead.");
              }
            }}
            data-testid="season-invite-copy"
            className="mt-2 w-full text-xs font-bold text-[var(--text-secondary)] hover:text-[var(--primary)] py-1"
          >
            Or copy join link
          </button>
        </div>

        {/* Big Team A vs Team B score */}
        <div className="surface-card p-6 mb-5" data-testid="season-scoreboard">
          <div className="grid grid-cols-3 items-center">
            <div className={`text-center ${aWinning ? "text-[var(--primary)]" : "text-[var(--text-secondary)]"}`}>
              <p className="text-[10px] uppercase tracking-[2px] font-bold">{season.team_a_name}</p>
              <p className="font-mono-num font-black text-5xl leading-none mt-1" data-testid="season-team-a-points">
                {(s.team_a_points ?? 0).toFixed(1).replace(/\.0$/, "")}
              </p>
              {aWinning && <p className="text-[9px] text-green-300 font-bold uppercase mt-1">Leading</p>}
            </div>
            <div className="text-center font-heading font-black text-2xl text-[var(--text-secondary)]">vs</div>
            <div className={`text-center ${bWinning ? "text-[var(--accent-terracotta)]" : "text-[var(--text-secondary)]"}`}>
              <p className="text-[10px] uppercase tracking-[2px] font-bold">{season.team_b_name}</p>
              <p className="font-mono-num font-black text-5xl leading-none mt-1" data-testid="season-team-b-points">
                {(s.team_b_points ?? 0).toFixed(1).replace(/\.0$/, "")}
              </p>
              {bWinning && <p className="text-[9px] text-orange-300 font-bold uppercase mt-1">Leading</p>}
            </div>
          </div>
        </div>

        {/* Per-player breakdown */}
        <div className="grid grid-cols-2 gap-3 mb-5" data-testid="season-rosters">
          <RosterCard team={season.team_a_name} tint="primary" players={s.team_a_players || []} testId="team-a" />
          <RosterCard team={season.team_b_name} tint="terracotta" players={s.team_b_players || []} testId="team-b" />
        </div>

        {/* Match log */}
        <h2 className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)] mb-2">Matches</h2>
        {(s.matches || []).length === 0 ? (
          <p className="text-sm italic text-[var(--text-secondary)]" data-testid="season-matches-empty">
            No matches counted yet. Play a match-play or fourball round with players from both teams to see results here.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="season-matches-list">
            {s.matches.map((m) => (
              <div key={m.round_id} className="surface-card p-2.5 flex items-center gap-2 text-xs" data-testid={`season-match-${m.round_id}`}>
                <span
                  className="w-8 text-center font-mono-num font-black text-sm"
                  style={{
                    color: m.result === "a" ? "var(--primary)" :
                           m.result === "b" ? "var(--accent-terracotta)" :
                           "var(--text-secondary)",
                  }}
                >
                  {m.result === "a" ? "A" : m.result === "b" ? "B" : "="}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{m.course_name || "Course"}</p>
                  <p className="text-[10px] text-[var(--text-secondary)] font-mono-num">
                    {new Date(m.completed_at).toLocaleDateString("en-GB")} · {m.mode || "match"} · {m.margin}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {season.is_owner && (
          <button
            type="button"
            onClick={remove}
            data-testid="season-delete-btn"
            className="mt-6 w-full text-xs text-[var(--accent-terracotta)] py-2 opacity-70 hover:opacity-100"
          >
            Delete season
          </button>
        )}
      </div>
    </div>
  );
}

function RosterCard({ team, tint, players, testId }) {
  const tintColor = tint === "primary" ? "var(--primary)" : "var(--accent-terracotta)";
  return (
    <div className="surface-card p-3" data-testid={`season-roster-${testId}`}>
      <p className="text-[10px] uppercase tracking-[2px] font-bold mb-2" style={{ color: tintColor }}>{team}</p>
      {players.length === 0 && <p className="text-[10px] italic text-[var(--text-secondary)]">No players yet.</p>}
      {players.map((p) => (
        <div key={p.id} className="flex items-center gap-1.5 py-1 border-t border-white/5 first:border-t-0" data-testid={`season-player-${p.id}`}>
          <span className="flex-1 text-xs font-bold truncate">{p.name}</span>
          <span className="text-[9px] font-mono-num text-[var(--text-secondary)]">
            {p.wins || 0}W · {p.losses || 0}L · {p.halved || 0}H
          </span>
        </div>
      ))}
    </div>
  );
}
